import * as React from "react";
import {AppBar, Box, Dialog, DialogContent, IconButton, Toolbar, Tooltip, Typography} from "@mui/material";
import {Close} from "@mui/icons-material";

export interface FullscreenTableDialogProps {
    /** Whether the dialog is open */
    open: boolean;
    /** Called when the dialog should close (X button or Esc key) */
    onClose: () => void;
    /** Large heading displayed in the dialog header (e.g. table name) */
    title: string;
    /** Tooltip / aria-label text for the close button */
    closeLabel: string;
    /** The table element to render in the body */
    tableNode: React.ReactNode;
}

/**
 * Full-screen dialog that wraps a table with a prominent header containing a large title on the left and a
 * close button in the top-right corner. Pressing Esc also closes the dialog.
 *
 * No longer takes a status filter below the title (#1607): the inline status filter this was built for is
 * gone from every table variant, replaced by the shared `OverviewFilterBar` and the column-header filter
 * popovers rendered inside `tableNode` itself. See the doc comment on `DataTableColumnHeader` for why those
 * popovers paint above this fullscreen dialog with no extra `zIndex` offset.
 */
export const FullscreenTableDialog: React.FC<FullscreenTableDialogProps> = ({
    open,
    onClose,
    title,
    closeLabel,
    tableNode
}) => (
        <Dialog fullScreen open={open} onClose={onClose}>
            <AppBar position="sticky" color="default" elevation={1}>
                <Toolbar sx={{ alignItems: "flex-start", paddingTop: 1.5, paddingBottom: 1.5 }}>
                    <Box flexGrow={1}>
                        <Typography variant="h5" component="h1">
                            {title}
                        </Typography>
                    </Box>
                    <Tooltip title={closeLabel}>
                        <IconButton edge="end" onClick={onClose} aria-label={closeLabel} sx={{ marginTop: 0.5 }}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>
            <DialogContent sx={{ padding: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                    {tableNode}
                </Box>
            </DialogContent>
        </Dialog>
    );
